<template>
  <el-dialog
    class="dialogByTravelAnalysis"
    append-to-body
    v-loading="loading"
    title="活動數據分析報告"
    top="3%"
    :visible.sync="innerVisible"
    width="90%"
  >
    <el-card class="box-card">
      <el-row>
        <el-form :inline="true" class="filter-form" @submit.native.prevent>
          <el-form-item label="查詢">
            <el-input
              v-model="search.qry"
              placeholder="輸入團號或團名"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>

          <el-button
            type="primary"
            @click="handleSearch"
            icon="el-icon-search"
            circle
            style="margin-top: 8px; margin-left: 5px"
            :disabled="loading"
          ></el-button>
          <el-button
            style="cursor: pointer"
            type="success"
            :loading="loading"
            circle
            icon="el-icon-refresh-left"
            @click="handleRefresh"
          ></el-button>
        </el-form>
      </el-row>

      <el-row>
        <el-table
          size="mini"
          :data="list"
          border
          style="width: 100%"
          height="calc(60vh - 80px)"
          overflow-y="auto"
        >
          <el-table-column align="center" label="項次" width="60">
            <template slot-scope="scope">
              <span>{{ (pagination.page - 1) * pagination.limit + scope.$index + 1 }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="tourGroupNo" label="團號" width="200" align="center" />

          <el-table-column align="center" label="團名" width="450">
            <template slot-scope="{ row }">
              <span>{{ row.travelName }}</span>
              <div>
                <el-tag style="font-size: 14px">
                  出發
                  <span style="color: red">{{ `${row.beginDate} ${row.beginTime}` }}</span>
                  - 回程
                  <span style="color: red">{{ `${row.endDate} ${row.endTime}` }}</span>
                </el-tag>
              </div>
            </template>
          </el-table-column>

          <el-table-column align="center" label="查看報告" width="100">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.analysisReportUrl"
                size="mini"
                type="info"
                @click="handleViewReport(scope.row)"
              >
                查看
              </el-button>
              <el-button
                v-if="!scope.row.analysisReportUrl"
                size="mini"
                type="success"
                @click="handleGenerateReport(scope.row)"
              >
                生成
              </el-button>
            </template>
          </el-table-column>

          <el-table-column v-if="!isProdMode" align="center" label="" width="100">
            <template slot-scope="scope">
              <el-button
                v-if="!isProdMode && scope.row.analysisReportUrl"
                size="mini"
                type="success"
                @click="handleGenerateReport(scope.row)"
              >
                重新生成
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div style="width: 100%; overflow: auto">
          <pagination
            v-show="pagination.total > 0"
            layout="prev, pager, next"
            :total="pagination.total"
            :page.sync="pagination.page"
            :limit.sync="pagination.limit"
            @pagination="handlePagination"
          />
        </div>
      </el-row>
    </el-card>

    <span slot="footer" class="dialog-footer action-buttons">
      <el-button @click="innerVisible = false">關 閉</el-button>
    </span>
  </el-dialog>
</template>

<script>
import Pagination from '@/components/Pagination'
import { getAgencyTravels } from '@/api/agency'
import { getTravelAnalysisToPdf, generateTravelAnalysisReportToPdf } from '@/api/travel'

export default {
  name: 'TravelAnalysisDialog',
  components: {
    Pagination,
  },
  props: {
    // 父組件只控制彈窗顯示，其餘資料皆內聚
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isProdMode: process.env.NODE_ENV == 'production',
      loading: false,
      aiLoading: false,
      list: [],
      sort: '-buildDate',
      search: {
        qry: '',
        date: '',
        status: null,
      },
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
      },
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
  },
  watch: {
    // 當彈窗被開啟時，才去撈取 API
    visible(newVal) {
      if (newVal) {
        this.getTravels()
      }
    },
    async loading(newValue) {
      if (newValue) {
        try {
          await this.$store.dispatch('app/showDownloadLoading', true)
          await this.$store.dispatch('app/showDownloadLoadingText', '資料處理中，請稍後')
        } catch (error) {
          console.error('Loading 初始化失敗:', error)
          await this.$store.dispatch('app/showDownloadLoading', false)
        }
      } else {
        await this.$store.dispatch('app/showDownloadLoading', false)
      }
    },
    async aiLoading(newValue) {
      if (newValue) {
        try {
          await this.$store.dispatch('app/showAIProcessLoading', true)
          await this.$store.dispatch(
            'app/showAIProcessLoadingText',
            'AI生成分析報表中，請不要離開此畫面，感謝您的耐心等候。'
          )
        } catch (error) {
          console.error('Loading 初始化失敗:', error)
          await this.$store.dispatch('app/showAIProcessLoading', false)
        }
      } else {
        await this.$store.dispatch('app/showAIProcessLoading', false)
      }
    },
  },
  methods: {
    async getTravels(showLoading = true) {
      if (showLoading) {
        this.loading = true
      }
      try {
        let res = await getAgencyTravels({
          ...this.search,
          page: this.pagination.page,
          limit: this.pagination.limit,
          sort: this.sort,
          statusMany: [3, 4],
        })
        // 此專案 webpack4/babel 不支援 optional chaining（?.），會導致整包 build 失敗
        this.list = (res.data && res.data.data) || []
        this.pagination.total = (res.data && res.data.total) || 0
      } catch (error) {
        console.error('撈取行程列表失敗:', error)
      } finally {
        this.loading = false
      }
    },
    async handleSearch() {
      this.pagination.page = 1
      await this.getTravels()
      this.$emit('search')
    },
    async handleRefresh() {
      this.search.qry = ''
      this.pagination.page = 1
      await this.getTravels()
      this.$emit('refresh')
    },
    async handlePagination() {
      await this.getTravels()
      this.$emit('change-page')
    },
    async handleViewReport(row) {
      this.loading = true
      try {
        const res = await getTravelAnalysisToPdf(row.id)
        const blob = new Blob([res], { type: 'application/pdf' })
        const fileURL = URL.createObjectURL(blob)

        window.open(fileURL, '_blank')
        setTimeout(() => URL.revokeObjectURL(fileURL), 100)

        this.$emit('view-report', row)
      } catch (error) {
        console.error('預覽 PDF 失敗:', error)
      } finally {
        this.loading = false
      }
    },
    async handleGenerateReport(row) {
      this.aiLoading = true
      try {
        await generateTravelAnalysisReportToPdf(row.id)

        await this.getTravels(false)

        this.$message({
          message: `分析報表生成完成: 團號:${row.tourGroupNo} 團名:${row.travelName}`,
          type: 'success',
        })

        this.$emit('generate-report', row)
      } catch (error) {
        console.error('生成 PDF 失敗:', error)
      } finally {
        this.aiLoading = false
      }
    },
  },
}
</script>
